import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  RadioGroup,
  Radio,
  Avatar,
  Stack,
  useTheme,
  Slider,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Checkbox,
  FormGroup,
  InputAdornment,
  ClickAwayListener
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import PhoneIcon from '@mui/icons-material/Phone';
import WebIcon from '@mui/icons-material/Web';
import CodeIcon from '@mui/icons-material/Code';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BusinessIcon from '@mui/icons-material/Business';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PersonIcon from '@mui/icons-material/Person';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import LanguageIcon from '@mui/icons-material/Language';
import SpeedIcon from '@mui/icons-material/Speed';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsIcon from '@mui/icons-material/Sms';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TuneIcon from '@mui/icons-material/Tune';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import EmailIcon from '@mui/icons-material/Email';
import ApiIcon from '@mui/icons-material/Api';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BadgeIcon from '@mui/icons-material/Badge';
import HomeIcon from '@mui/icons-material/Home';
import PublicIcon from '@mui/icons-material/Public';
import FlagIcon from '@mui/icons-material/Flag';
import { useNavigate } from 'react-router-dom';
import { createAuthHeaders, handleTokenExpiration, isTokenExpired } from '../utils/auth';
import { useAuth } from '../context/AuthContext';

const PlugAndPlay = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [success, setSuccess] = useState(false);
  
  // AI conversion loading states
  const [isConverting, setIsConverting] = useState(false);
  const [conversionProgress, setConversionProgress] = useState('');
  const [extractingText, setExtractingText] = useState(false);
  const [convertingWithAI, setConvertingWithAI] = useState(false);
  
  const [formData, setFormData] = useState({
    agentName: '',
    businessType: '',
    deploymentType: 'phone',
    phoneNumberType: '',
    language: 'en-US',
    voice: 'en-US-JennyNeural',
    personality: 'professional',
    useCase: '',
    knowledgeBase: null,
    knowledgeBaseFile: null,
    knowledgeBaseText: '',
    knowledgeBaseInputType: 'file', // 'file' or 'text'
    customPrompt: '',
    llmModel: 'gpt-4o',
    temperature: 0.7,
    speechSpeed: 100,
    handlesOrders: false,
    handlesReservations: false,
    orderTypes: ['pickup', 'delivery'], // pickup, delivery, or both
    orderSystem: '',
    reservationSystem: '',
    orderApiKey: '',
    reservationApiKey: '',
    notificationEmail: '',
    companyName: '',
    region: '',
    identificationDocument: null,
    companyRegistrationProof: null,
    personalId: null,
    companyAddress: '',
    country: 'Netherlands',
    customWelcomeMessage: '',
    orderDataFields: ['customer_name', 'customer_phone', 'order_items', 'delivery_address'],
    reservationDataFields: ['customer_name', 'customer_phone', 'reservation_date', 'reservation_time', 'party_size'],
    humanEscalationEnabled: false,
    humanEscalationPhone: '',
    humanEscalationCountry: 'us'
  });

  const businessTypes = [
    { 
      value: 'restaurant', 
      label: 'Restaurant', 
      icon: <RestaurantIcon />,
      description: 'Handle reservations, takeout orders, and customer inquiries'
    },
    { 
      value: 'healthcare', 
      label: 'Healthcare', 
      icon: <LocalHospitalIcon />,
      description: 'Appointment scheduling and patient information'
    },
    { 
      value: 'business', 
      label: 'General Business', 
      icon: <BusinessIcon />,
      description: 'Customer support and general inquiries'
    },
    { 
      value: 'support', 
      label: 'Customer Support', 
      icon: <SupportAgentIcon />,
      description: 'Technical support and help desk services'
    }
  ];

  const phoneNumberOptions = [
    {
      value: 'mobile',
      label: 'Mobile Number',
      icon: <PhoneAndroidIcon />,
      description: 'Mobile number with SMS and WhatsApp support',
      features: ['Voice calls', 'SMS messaging', 'WhatsApp integration'],
      price: '€25/month',
      popular: true,
      businessHint: 'Perfect for transferring your existing company number automatically - customers can reach your AI agent instantly'
    },
    {
      value: 'local',
      label: 'Local Number',
      icon: <LocalPhoneIcon />,
      description: 'Local landline number (voice calls only)',
      features: ['Voice calls only', 'No SMS or WhatsApp'],
      price: '€15/month',
      requiresVerification: true,
      businessHint: 'Ideal for replacing your public company number completely - gives customers a professional local presence'
    },
    {
      value: 'combo',
      label: 'Local + Mobile Combo',
      icon: <CallMergeIcon />,
      description: 'Best of both worlds - local presence with mobile features',
      features: ['Local & mobile numbers', 'SMS messaging', 'WhatsApp integration', 'Professional presence'],
      price: '€35/month',
      requiresVerification: true,
      businessHint: 'Best of both worlds - professional local number for your business listing plus mobile for customer convenience'
    }
  ];

  const deploymentOptions = [
    {
      value: 'phone',
      label: 'Phone Number',
      icon: <PhoneIcon />,
      description: 'Customers call a dedicated phone number',
      popular: true
    },
    {
      value: 'web',
      label: 'Website Widget',
      icon: <WebIcon />,
      description: 'Embed voice chat widget on your website'
    },
    {
      value: 'api',
      label: 'API Integration',
      icon: <CodeIcon />,
      description: 'Custom integration via API endpoints'
    }
  ];

  const orderSystems = [
    { value: 'sitedish', label: 'SiteDish', description: 'Leading Dutch food ordering platform' },
    { value: 'flipdish', label: 'FlipDish', description: 'International food ordering system' },
    { value: 'other', label: 'Other/Email', description: 'Email notifications for orders' }
  ];

  const reservationSystems = [
    { value: 'zenchef', label: 'Zenchef', description: 'Restaurant reservation management' },
    { value: 'defork', label: 'DeFork', description: 'Table booking platform' },
    { value: 'other', label: 'Other/Email', description: 'Email notifications for reservations' }
  ];

  const modelOptions = [
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo', description: 'Fast and cost-effective' },
    { value: 'gpt-3.5-turbo-16k', label: 'GPT-3.5 Turbo (16k)', description: 'Extended context length' },
    { value: 'gpt-4', label: 'GPT-4', description: 'Most capable model' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo', description: 'Enhanced performance' },
    { value: 'gpt-4o', label: 'GPT-4o', description: 'Latest optimized model' },
    { value: 'gpt-4.5-preview', label: 'GPT-4.5 Preview', description: 'Next generation preview' },
    { value: 'o1-preview', label: 'o1 Preview', description: 'Advanced reasoning model' },
    { value: 'o1-mini', label: 'o1 Mini', description: 'Efficient reasoning model' }
  ];

  const dutchRegions = [
    'Noord-Holland', 'Zuid-Holland', 'Noord-Brabant', 'Gelderland', 'Utrecht', 
    'Limburg', 'Overijssel', 'Groningen', 'Friesland', 'Drenthe', 'Zeeland', 'Flevoland'
  ];

  const countries = [
    'Netherlands', 'Belgium', 'Germany', 'France', 'United Kingdom', 'Spain', 'Italy', 
    'Portugal', 'Austria', 'Switzerland', 'Poland', 'Czech Republic', 'Denmark', 
    'Sweden', 'Norway', 'Finland', 'Other'
  ];

  const personalityOptions = [
    { value: 'professional', label: 'Professional', description: 'Formal and business-like' },
    { value: 'friendly', label: 'Friendly', description: 'Warm and approachable' },
    { value: 'casual', label: 'Casual', description: 'Relaxed and conversational' },
    { value: 'energetic', label: 'Energetic', description: 'Upbeat and enthusiastic' }
  ];

  const languageOptions = [
    { value: 'en-US', label: 'English (US)', flag: '🇺🇸' },
    { value: 'en-GB', label: 'English (UK)', flag: '🇬🇧' },
    { value: 'en-AU', label: 'English (Australia)', flag: '🇦🇺' },
    { value: 'es-ES', label: 'Spanish', flag: '🇪🇸' },
    { value: 'fr-FR', label: 'French', flag: '🇫🇷' },
    { value: 'de-DE', label: 'German', flag: '🇩🇪' },
    { value: 'it-IT', label: 'Italian', flag: '🇮🇹' },
    { value: 'pt-BR', label: 'Portuguese (Brazil)', flag: '🇧🇷' },
    { value: 'nl-NL', label: 'Dutch', flag: '🇳🇱' },
    { value: 'ja-JP', label: 'Japanese', flag: '🇯🇵' },
    { value: 'ko-KR', label: 'Korean', flag: '🇰🇷' },
    { value: 'zh-CN', label: 'Chinese (Simplified)', flag: '🇨🇳' },
    { value: 'ru-RU', label: 'Russian', flag: '🇷🇺' },
    { value: 'ar-SA', label: 'Arabic', flag: '🇸🇦' }
  ];

  // Helper function to get country options with flags
  const getCountryOptions = () => [
    { code: "us", name: "United States", dial_code: "+1" },
    { code: "gb", name: "United Kingdom", dial_code: "+44" },
    { code: "ca", name: "Canada", dial_code: "+1" },
    { code: "au", name: "Australia", dial_code: "+61" },
    { code: "de", name: "Germany", dial_code: "+49" },
    { code: "fr", name: "France", dial_code: "+33" },
    { code: "it", name: "Italy", dial_code: "+39" },
    { code: "es", name: "Spain", dial_code: "+34" },
    { code: "pt", name: "Portugal", dial_code: "+351" },
    { code: "nl", name: "Netherlands", dial_code: "+31" },
    { code: "be", name: "Belgium", dial_code: "+32" },
    { code: "ch", name: "Switzerland", dial_code: "+41" },
    { code: "at", name: "Austria", dial_code: "+43" },
    { code: "dk", name: "Denmark", dial_code: "+45" },
    { code: "se", name: "Sweden", dial_code: "+46" },
    { code: "no", name: "Norway", dial_code: "+47" },
    { code: "fi", name: "Finland", dial_code: "+358" },
    { code: "pl", name: "Poland", dial_code: "+48" },
    { code: "cz", name: "Czech Republic", dial_code: "+420" },
    { code: "jp", name: "Japan", dial_code: "+81" },
    { code: "kr", name: "South Korea", dial_code: "+82" },
    { code: "cn", name: "China", dial_code: "+86" },
    { code: "br", name: "Brazil", dial_code: "+55" },
    { code: "mx", name: "Mexico", dial_code: "+52" },
    { code: "ar", name: "Argentina", dial_code: "+54" },
    { code: "in", name: "India", dial_code: "+91" },
    { code: "sg", name: "Singapore", dial_code: "+65" },
    { code: "za", name: "South Africa", dial_code: "+27" }
  ];

  // Enhanced country selector component with search and flags
  const CountrySelector = ({ value, onChange, ...props }) => {
    const countries = getCountryOptions();
    const [searchText, setSearchText] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // Find the current selected country
    const selectedCountry = countries.find(c => c.code === value) || countries.find(c => c.code === 'us');

    // Filter countries based on search text
    const filteredCountries = searchText 
      ? countries.filter(c => 
          c.name.toLowerCase().includes(searchText.toLowerCase()) || 
          c.dial_code.includes(searchText)
        )
      : countries;

    return (
      <Box sx={{ position: 'relative', ...props.sx }}>
        <Box 
          onClick={() => setDropdownOpen(!dropdownOpen)} 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: '4px',
            p: 1, 
            cursor: 'pointer',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
            height: props.small ? '40px' : '56px',
          }}
        >
          <Box component="img" 
            src={`https://flagcdn.com/w20/${selectedCountry?.code.toLowerCase()}.png`} 
            alt={selectedCountry?.name}
            sx={{ mr: 1, width: 20, height: 'auto' }}
          />
          <Typography variant={props.small ? "body2" : "body1"}>
            {selectedCountry?.dial_code}
          </Typography>
        </Box>

        {dropdownOpen && (
          <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
            <Box sx={{
              position: 'absolute',
              top: props.small ? '45px' : '60px',
              left: 0,
              zIndex: 10,
              width: '280px',
              maxHeight: '300px',
              overflowY: 'auto',
              bgcolor: 'background.paper',
              boxShadow: 3,
              borderRadius: 1,
              p: 1
            }}>
              <TextField
                size="small"
                placeholder="Search countries..."
                fullWidth
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{ mb: 1 }}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FlagIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Grid container spacing={1}>
                {filteredCountries.map(country => (
                  <Grid item xs={6} key={country.code}>
                    <Button
                      fullWidth
                      onClick={() => {
                        onChange(country.code);
                        setDropdownOpen(false);
                      }}
                      startIcon={<Box component="img" src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`} alt={country.name} sx={{ mr: 1 }} />}
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      {country.name}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </ClickAwayListener>
        )}
      </Box>
    );
  };

  const getVoiceOptions = (language) => {
    const voiceMap = {
      'en-US': [
        { value: 'en-US-JennyNeural', label: 'Jenny (Female)' },
        { value: 'en-US-GuyNeural', label: 'Guy (Male)' },
        { value: 'en-US-AriaNeural', label: 'Aria (Female)' },
        { value: 'en-US-AmberNeural', label: 'Amber (Female)' },
      ],
      'en-GB': [
        { value: 'en-GB-LibbyNeural', label: 'Libby (Female)' },
        { value: 'en-GB-RyanNeural', label: 'Ryan (Male)' },
        { value: 'en-GB-MiaNeural', label: 'Mia (Female)' },
      ],
      'en-AU': [
        { value: 'en-AU-NatashaNeural', label: 'Natasha (Female)' },
        { value: 'en-AU-WilliamNeural', label: 'William (Male)' },
      ],
      'fr-FR': [
        { value: 'fr-FR-DeniseNeural', label: 'Denise (Female)' },
        { value: 'fr-FR-HenriNeural', label: 'Henri (Male)' },
      ],
      'de-DE': [
        { value: 'de-DE-KatjaNeural', label: 'Katja (Female)' },
        { value: 'de-DE-ConradNeural', label: 'Conrad (Male)' },
      ],
      'es-ES': [
        { value: 'es-ES-ElviraNeural', label: 'Elvira (Female)' },
        { value: 'es-ES-AlvaroNeural', label: 'Alvaro (Male)' },
      ],
      'it-IT': [
        { value: 'it-IT-IsabellaNeural', label: 'Isabella (Female)' },
        { value: 'it-IT-DiegoNeural', label: 'Diego (Male)' },
      ],
      'ja-JP': [
        { value: 'ja-JP-NanamiNeural', label: 'Nanami (Female)' },
        { value: 'ja-JP-KeitaNeural', label: 'Keita (Male)' },
      ],
      'zh-CN': [
        { value: 'zh-CN-XiaoxiaoNeural', label: 'Xiaoxiao (Female)' },
        { value: 'zh-CN-YunjianNeural', label: 'Yunjian (Male)' },
      ],
      'pt-BR': [
        { value: 'pt-BR-FranciscaNeural', label: 'Francisca (Female)' },
        { value: 'pt-BR-AntonioNeural', label: 'Antonio (Male)' },
      ],
      'nl-NL': [
        { value: 'nl-NL-FennaNeural', label: 'Fenna (Female)' },
        { value: 'nl-NL-MaartenNeural', label: 'Maarten (Male)' },
      ],
      'ru-RU': [
        { value: 'ru-RU-SvetlanaNeural', label: 'Svetlana (Female)' },
        { value: 'ru-RU-DmitryNeural', label: 'Dmitry (Male)' },
      ],
      'ko-KR': [
        { value: 'ko-KR-SunHiNeural', label: 'SunHi (Female)' },
        { value: 'ko-KR-InJoonNeural', label: 'InJoon (Male)' },
      ],
      'ar-SA': [
        { value: 'ar-SA-ZariyahNeural', label: 'Zariyah (Female)' },
        { value: 'ar-SA-HamedNeural', label: 'Hamed (Male)' },
      ],
    };
   
    return voiceMap[language] || voiceMap['en-US'];
  };



  const convertToKnowledgeBase = async (content, inputType = 'file') => {
    try {
      setIsConverting(true);
      
      // Prepare the content for ChatGPT based on input type
      let textContent = '';
      
      if (inputType === 'text') {
        textContent = content;
        setConversionProgress('Preparing text content...');
        // Add a small delay to show the progress
        await new Promise(resolve => setTimeout(resolve, 500));
      } else if (inputType === 'file') {
        // For files, we would extract text using the backend API
        setExtractingText(true);
        setConversionProgress(`Extracting text from ${content.name}...`);
        
        const formData = new FormData();
        formData.append('file', content);
        
        const extractResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/knowledge-base/extract-text`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: formData
        });
        
        if (extractResponse.ok) {
          const extractedData = await extractResponse.json();
          textContent = extractedData.text_content; // Backend returns 'text_content', not 'text'
          setExtractingText(false);
          setConversionProgress('Text extracted successfully! ✅');
          await new Promise(resolve => setTimeout(resolve, 800));
        } else {
          setExtractingText(false);
          throw new Error('Failed to extract text from file');
        }
      }

      // Call ChatGPT API to convert content to structured JSON
      setConvertingWithAI(true);
      setConversionProgress('Converting with ChatGPT AI... 🤖');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/knowledge-base/convert-with-ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          content: textContent,
          content_type: 'auto', // Backend expects 'content_type', not 'inputType'
          filename: inputType === 'file' ? content.name : 'text_input' // Backend expects 'filename', not 'fileName'
        })
      });

      if (response.ok) {
        const knowledgeBase = await response.json();
        setConvertingWithAI(false);
        setConversionProgress('Knowledge base created successfully! 🎉');
        await new Promise(resolve => setTimeout(resolve, 1000));
        return knowledgeBase;
      } else {
        setConvertingWithAI(false);
        throw new Error('Failed to convert content to knowledge base');
      }
    } catch (error) {
      console.log('ChatGPT conversion failed, using fallback data for:', inputType === 'file' ? content.name : 'text input');
      console.error('Conversion error:', error);
      
      // Reset loading states
      setExtractingText(false);
      setConvertingWithAI(false);
      setConversionProgress('Using fallback data... ⚠️');
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Return fallback mock knowledge base structure with metadata indicating it's mock data
      return {
        restaurant: {
          name: inputType === 'file' ? content.name.replace(/\.[^/.]+$/, "") : "Your Business",
          cuisine: "Modern Cuisine",
          description: "A contemporary dining experience"
        },
        categories: [
          {
            id: "items",
            name: "MENU ITEMS",
            items: [
              {
                id: "item1",
                name: "Sample Item",
                description: "Delicious menu item",
                price: 15.00,
                dietary: ["gluten-free"],
                available: true
              }
            ]
          }
        ],
        hours: {
          monday: "9:00 AM - 9:00 PM",
          tuesday: "9:00 AM - 9:00 PM",
          wednesday: "9:00 AM - 9:00 PM",
          thursday: "9:00 AM - 9:00 PM",
          friday: "9:00 AM - 10:00 PM",
          saturday: "9:00 AM - 10:00 PM",
          sunday: "10:00 AM - 8:00 PM"
        },
        contact: {
          phone: "Phone number to be configured",
          email: "info@yourbusiness.com",
          address: "Your business address"
        },
        _metadata: {
          generated_with_ai: false,
          fallback_reason: error.message,
          source: inputType === 'file' ? content.name : 'text input',
          backend_available: false
        }
      };
    } finally {
      // Always reset loading states
      setIsConverting(false);
      setExtractingText(false);
      setConvertingWithAI(false);
      setConversionProgress('');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, knowledgeBaseFile: file, knowledgeBaseInputType: 'file' }));
      // Don't automatically process - wait for user to click "Process with AI"
    }
  };

  const handleTextConversion = async () => {
    if (formData.knowledgeBaseText.trim()) {
      try {
        const knowledgeBase = await convertToKnowledgeBase(formData.knowledgeBaseText, 'text');
        setFormData(prev => ({ ...prev, knowledgeBase }));
        
        // Check if this was generated with AI or fallback data
        if (knowledgeBase._metadata?.generated_with_ai === false) {
          setSnackbar({
            open: true,
            message: 'Using sample knowledge base (AI conversion unavailable - backend server needs restart)',
            severity: 'warning'
          });
        } else {
          setSnackbar({
            open: true,
            message: '🎉 Knowledge base created with AI successfully! Your content has been structured and is ready to use.',
            severity: 'success'
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to process text. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  const handleFileConversion = async () => {
    if (formData.knowledgeBaseFile) {
      try {
        const knowledgeBase = await convertToKnowledgeBase(formData.knowledgeBaseFile, 'file');
        setFormData(prev => ({ ...prev, knowledgeBase }));
        
        // Check if this was generated with AI or fallback data
        if (knowledgeBase._metadata?.generated_with_ai === false) {
          setSnackbar({
            open: true,
            message: 'Using sample knowledge base (AI conversion unavailable - backend server needs restart)',
            severity: 'warning'
          });
        } else {
          setSnackbar({
            open: true,
            message: '🎉 Knowledge base created from file with AI successfully! Your content has been extracted and structured.',
            severity: 'success'
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to process file. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  const handleDocumentUpload = (field) => (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData({ ...formData, [field]: file });
      setSnackbar({
        open: true,
        message: `${field === 'companyRegistrationProof' ? 'Company registration' : 'Personal ID'} uploaded successfully!`,
        severity: 'success'
      });
    }
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const validateCurrentStep = () => {
    switch (step) {
      case 1:
        return formData.agentName.length >= 3;
      case 2:
        if (!formData.phoneNumberType) return false;
        if ((formData.phoneNumberType === 'local' || formData.phoneNumberType === 'combo') && 
            (!formData.companyName || !formData.region || !formData.companyAddress || 
             !formData.country || !formData.companyRegistrationProof || !formData.personalId)) return false;
        return true;
      case 3:
        return true; // Knowledge base is optional
      case 4:
        return formData.language && formData.voice && formData.customPrompt.trim();
      case 5:
        if (formData.handlesOrders && formData.orderSystem === 'other' && !formData.notificationEmail) return false;
        if (formData.handlesReservations && formData.reservationSystem === 'other' && !formData.notificationEmail) return false;
        if (formData.handlesOrders && formData.orderSystem !== 'other' && !formData.orderApiKey) return false;
        if (formData.handlesReservations && formData.reservationSystem !== 'other' && !formData.reservationApiKey) return false;
        return true;
      default:
        return true;
    }
  };

  const handleDeploy = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/plugnplay/create`, {
        method: 'POST',
        headers: createAuthHeaders(),
        body: JSON.stringify({
          agentName: formData.agentName,
          phoneNumberType: formData.phoneNumberType,
          companyName: formData.companyName,
          companyAddress: formData.companyAddress,
          region: formData.region,
          country: formData.country,
          companyRegistrationProof: formData.companyRegistrationProof,
          personalId: formData.personalId,
          knowledgeBase: formData.knowledgeBase,
          knowledgeBaseText: formData.knowledgeBaseText,
          language: formData.language,
          voice: formData.voice,
          speechSpeed: formData.speechSpeed,
          llmModel: formData.llmModel,
          temperature: formData.temperature,
          customPrompt: formData.customPrompt,
          handlesOrders: formData.handlesOrders,
          orderTypes: formData.orderTypes,
          orderSystem: formData.orderSystem,
          orderApiKey: formData.orderApiKey,
          handlesReservations: formData.handlesReservations,
          reservationSystem: formData.reservationSystem,
          reservationApiKey: formData.reservationApiKey,
          notificationEmail: formData.notificationEmail,
          customWelcomeMessage: formData.customWelcomeMessage,
          orderDataFields: formData.orderDataFields,
          reservationDataFields: formData.reservationDataFields
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 && isTokenExpired(data)) {
          handleTokenExpiration();
          return;
        }
        throw new Error(data.error || 'Failed to create agent');
      }

      setSuccess(true);
      setSnackbar({
        open: true,
        message: 'Voice agent deployed successfully!',
        severity: 'success'
      });

      setTimeout(() => {
        navigate('/agents');
      }, 2000);
    } catch (error) {
      console.error('Error deploying agent:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to deploy agent. Please try again.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const renderStep1 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <RocketLaunchIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Let's Create Your Voice Agent
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Get your AI voice assistant up and running in just a few minutes
        </Typography>
      </Box>

      <Card sx={{ maxWidth: 500, mx: 'auto' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            What should we call your agent?
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., Restaurant Assistant, Support Bot, Booking Agent"
            value={formData.agentName}
            onChange={(e) => setFormData({ ...formData, agentName: e.target.value })}
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Choose a name that reflects your agent's purpose
          </Typography>
        </CardContent>
      </Card>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Choose Your Phone Number
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select the type of phone number for your voice agent
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {phoneNumberOptions.map((option) => (
          <Grid item xs={12} sm={4} key={option.value}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                border: formData.phoneNumberType === option.value ? 2 : 1,
                borderColor: formData.phoneNumberType === option.value ? 'primary.main' : 'divider',
                position: 'relative',
                '&:hover': { borderColor: 'primary.main' },
                height: '100%'
              }}
              onClick={() => setFormData({ ...formData, phoneNumberType: option.value })}
            >
              {option.popular && (
                <Chip 
                  label="Most Popular" 
                  size="small" 
                  color="primary" 
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                />
              )}
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  {option.icon}
                  <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
                    {option.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {option.description}
                  </Typography>
                  <Chip 
                    label={option.price} 
                    color="primary" 
                    variant="outlined" 
                    size="small"
                    sx={{ mb: 2 }}
                  />
                </Box>
                
                <List sx={{ flexGrow: 1 }}>
                  {option.features.map((feature, index) => {
                    const isNegativeFeature = feature.toLowerCase().includes('no ');
                    return (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {isNegativeFeature ? (
                            <CloseIcon color="error" fontSize="small" />
                          ) : (
                            <CheckCircleIcon color="success" fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature}
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            color: isNegativeFeature ? 'text.secondary' : 'text.primary'
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>

                {option.requiresVerification && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="caption">
                      Requires company verification
                    </Typography>
                  </Alert>
                )}

                {option.businessHint && (
                  <Alert severity="info" sx={{ mt: 2, bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                      💡 {option.businessHint}
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Verification Form for Local/Combo Numbers */}
      {(formData.phoneNumberType === 'local' || formData.phoneNumberType === 'combo') && (
        <Card sx={{ mt: 4, p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <VerifiedUserIcon sx={{ mr: 1 }} />
            Company Verification Required
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Local numbers require comprehensive verification for regulatory compliance. Please provide all required information and documents.
          </Typography>
          
          <Grid container spacing={3}>
            {/* Company Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Company Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name *"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                InputProps={{
                  startAdornment: <BusinessCenterIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Country *</InputLabel>
                <Select
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  startAdornment={<PublicIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                >
                  {countries.map((country) => (
                    <MenuItem key={country} value={country}>{country}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Region/State *</InputLabel>
                <Select
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  startAdornment={<LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                  disabled={formData.country !== 'Netherlands'}
                >
                  {formData.country === 'Netherlands' ? (
                    dutchRegions.map((region) => (
                      <MenuItem key={region} value={region}>{region}</MenuItem>
                    ))
                  ) : (
                    <MenuItem value="other">Other</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Address *"
                value={formData.companyAddress}
                onChange={(e) => setFormData({ ...formData, companyAddress: e.target.value })}
                InputProps={{
                  startAdornment: <HomeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                placeholder="Street, City, Postal Code"
                required
              />
            </Grid>

            {/* Document Uploads */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Required Documents
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload clear copies of the following documents (PDF, JPG, PNG accepted)
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, border: '1px dashed', borderColor: 'primary.main', bgcolor: 'primary.light', bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
                  Company Registration Proof *
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Chamber of Commerce registration, business license, or equivalent
                </Typography>
                
                <input
                  accept=".pdf,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  id="company-registration-upload"
                  type="file"
                  onChange={handleDocumentUpload('companyRegistrationProof')}
                />
                <label htmlFor="company-registration-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AttachFileIcon />}
                    size="small"
                    fullWidth
                  >
                    Upload Document
                  </Button>
                </label>
                
                {formData.companyRegistrationProof && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                    ✅ {formData.companyRegistrationProof.name}
                  </Typography>
                )}
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, border: '1px dashed', borderColor: 'primary.main', bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
                  Personal Identification *
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Passport, national ID card, or driver's license
                </Typography>
                
                <input
                  accept=".pdf,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  id="personal-id-upload"
                  type="file"
                  onChange={handleDocumentUpload('personalId')}
                />
                <label htmlFor="personal-id-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<BadgeIcon />}
                    size="small"
                    fullWidth
                  >
                    Upload ID
                  </Button>
                </label>
                
                {formData.personalId && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                    ✅ {formData.personalId.name}
                  </Typography>
                )}
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Processing Time:</strong> Verification typically takes 1-3 business days. 
                  You'll receive email notifications about the status of your application.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </Card>
      )}
    </motion.div>
  );

  const renderStep3 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Setup Knowledge Base
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload your menu, service information, or any content your agent should know about
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Input Type Selection */}
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Choose Input Method
            </Typography>
            <FormControlLabel
              control={
                <Radio
                  checked={formData.knowledgeBaseInputType === 'file'}
                  onChange={() => setFormData(prev => ({ ...prev, knowledgeBaseInputType: 'file' }))}
                />
              }
              label="Upload File (PDF, TXT, DOCX, Images)"
            />
            <FormControlLabel
              control={
                <Radio
                  checked={formData.knowledgeBaseInputType === 'text'}
                  onChange={() => setFormData(prev => ({ ...prev, knowledgeBaseInputType: 'text' }))}
                />
              }
              label="Enter Text Directly"
            />
          </Card>
        </Grid>

        {/* File Upload Option */}
        {formData.knowledgeBaseInputType === 'file' && (
          <Grid item xs={12}>
            <Card sx={{ p: 4, textAlign: 'center', border: '2px dashed', borderColor: 'divider' }}>
              <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Upload Your Content
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Supported formats: PDF, TXT, DOCX, Images (JPG, PNG)
              </Typography>
              
              <input
                accept=".pdf,.txt,.docx,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                onChange={handleFileUpload}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<UploadFileIcon />}
                  size="large"
                >
                  Choose File
                </Button>
              </label>

              {formData.knowledgeBaseFile && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    ✅ {formData.knowledgeBaseFile.name} uploaded successfully
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 2 }}
                    onClick={handleFileConversion}
                    disabled={isConverting}
                    startIcon={isConverting ? <CircularProgress size={16} /> : <SmartToyIcon />}
                  >
                    {isConverting ? 'Processing...' : 'Process with AI'}
                  </Button>
                </Alert>
              )}

              {/* Conversion Progress Indicator */}
              {isConverting && (
                <Card sx={{ mt: 3, p: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CircularProgress size={24} sx={{ mr: 2, color: 'inherit' }} />
                    <Typography variant="h6">
                      AI Processing in Progress...
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {conversionProgress}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Chip 
                      label="Extract Text" 
                      color={extractingText ? "secondary" : "default"}
                      icon={extractingText ? <CircularProgress size={16} /> : (formData.knowledgeBase ? <CheckIcon /> : <HourglassEmptyIcon />)}
                      variant={extractingText ? "filled" : "outlined"}
                    />
                    <Chip 
                      label="AI Conversion" 
                      color={convertingWithAI ? "secondary" : "default"}
                      icon={convertingWithAI ? <CircularProgress size={16} /> : (formData.knowledgeBase ? <CheckIcon /> : <HourglassEmptyIcon />)}
                      variant={convertingWithAI ? "filled" : "outlined"}
                    />
                  </Box>
                  
                  <LinearProgress sx={{ mt: 2 }} />
                </Card>
              )}
            </Card>
          </Grid>
        )}

        {/* Text Input Option */}
        {formData.knowledgeBaseInputType === 'text' && (
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Enter Your Business Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Describe your menu, services, hours, location, or any information your agent should know
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={8}
                placeholder="Example: We are an Italian restaurant located in Amsterdam. Our specialties include homemade pasta, wood-fired pizza, and authentic gelato. We're open Tuesday-Sunday, 5 PM to 11 PM. We offer delivery through Deliveroo and takeout orders. Our signature dishes include Pasta Carbonara (€16), Margherita Pizza (€14), and Tiramisu (€8)..."
                value={formData.knowledgeBaseText}
                onChange={(e) => setFormData(prev => ({ ...prev, knowledgeBaseText: e.target.value }))}
                sx={{ mb: 2 }}
              />
              
              <Button
                variant="contained"
                onClick={handleTextConversion}
                disabled={!formData.knowledgeBaseText.trim() || isConverting}
                startIcon={isConverting ? <CircularProgress size={16} /> : <SmartToyIcon />}
              >
                {isConverting ? 'Converting...' : 'Convert to Knowledge Base with AI'}
              </Button>

              {/* Progress indicator for text conversion */}
              {isConverting && formData.knowledgeBaseInputType === 'text' && (
                <Card sx={{ mt: 3, p: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CircularProgress size={24} sx={{ mr: 2, color: 'inherit' }} />
                    <Typography variant="h6">
                      AI Processing in Progress...
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {conversionProgress}
                  </Typography>
                  
                  <LinearProgress sx={{ mt: 2 }} />
                </Card>
              )}
            </Card>
          </Grid>
        )}

        {formData.knowledgeBase && (
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <MenuBookIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Generated Knowledge Base Preview
              </Typography>
              
              {formData.knowledgeBase._metadata?.generated_with_ai === false && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Using Sample Data:</strong> The AI conversion endpoints are not available (backend server needs restart). 
                    This is sample data for demonstration. Restart your backend server to enable real AI-powered conversion.
                  </Typography>
                </Alert>
              )}
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {formData.knowledgeBase._metadata?.generated_with_ai === false 
                  ? 'Sample knowledge base structure for demonstration. You can edit this later in the agent configuration.'
                  : 'AI has automatically structured your content. You can edit this later in the agent configuration.'}
              </Typography>
              
              <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, maxHeight: 400, overflow: 'auto' }}>
                <pre style={{ fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {JSON.stringify(formData.knowledgeBase, null, 2)}
                </pre>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {formData.knowledgeBase._metadata?.generated_with_ai === false 
                  ? '⚠️ Using sample data (AI conversion unavailable - restart backend server to enable real AI conversion)' 
                  : '✅ Generated using real AI conversion'}
              </Typography>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <Alert severity="info">
            <Typography variant="body2">
              💡 <strong>Optional Step:</strong> You can skip this and add content later through the agent configuration panel.
              The AI will still be able to handle basic conversations without a knowledge base.
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </motion.div>
  );

  const renderStep4 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Configure Your Agent
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Customize your agent's voice, personality, and behavior
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Language & Voice */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              <LanguageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Language & Voice
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Language</InputLabel>
              <Select
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
              >
                {languageOptions.map((lang) => (
                  <MenuItem key={lang.value} value={lang.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: 8 }}>{lang.flag}</span>
                      {lang.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Voice</InputLabel>
              <Select
                value={formData.voice}
                onChange={(e) => setFormData({ ...formData, voice: e.target.value })}
              >
                {getVoiceOptions(formData.language).map((voice) => (
                  <MenuItem key={voice.value} value={voice.value}>
                    {voice.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body2" gutterBottom>Speech Speed (%)</Typography>
            <TextField
              fullWidth
              type="number"
              value={formData.speechSpeed}
              onChange={(e) => setFormData({ ...formData, speechSpeed: parseInt(e.target.value) || 100 })}
              inputProps={{ min: 50, max: 200, step: 5 }}
              helperText="50-200% (100% is normal speed)"
              sx={{ mb: 2 }}
            />
          </Card>
        </Grid>

        {/* AI Model & Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              <SmartToyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              AI Model & Settings
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>AI Model</InputLabel>
              <Select
                value={formData.llmModel}
                onChange={(e) => setFormData({ ...formData, llmModel: e.target.value })}
              >
                {modelOptions.map((model) => (
                  <MenuItem key={model.value} value={model.value}>
                    <Box>
                      <Typography variant="body2">{model.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {model.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body2" gutterBottom>
              Temperature (Creativity)
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={formData.temperature}
              onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) || 0.7 })}
              inputProps={{ min: 0, max: 1, step: 0.1 }}
              helperText="0.0 (precise) to 1.0 (creative)"
              sx={{ mb: 2 }}
            />
          </Card>
        </Grid>

        {/* Custom Prompt */}
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <TuneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Custom Instructions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Describe how your agent should behave and what it should know about your business
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={8}
              placeholder="Example: You are a friendly restaurant assistant. You help customers with reservations and menu questions. Always be polite and ask if they need anything else. Our restaurant specializes in Italian cuisine and we're open Tuesday to Sunday from 5 PM to 10 PM. You can take orders, make reservations, answer questions about our menu, and provide information about our location and hours. If you cannot help with something, politely explain what you can do instead."
              value={formData.customPrompt}
              onChange={(e) => setFormData({ ...formData, customPrompt: e.target.value })}
              helperText="Provide detailed instructions for how your agent should behave and respond to customers"
            />
          </Card>
        </Grid>

        {/* Welcome Message */}
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <RecordVoiceOverIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Welcome Message
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Customize the first message your agent says to greet callers
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder={
                formData.language.startsWith('nl') ? 
                  "Goedendag, welkom bij [Restaurant Name]. Waarmee kan ik u vandaag van dienst zijn?" :
                formData.language.startsWith('es') ?
                  "Hola, bienvenido a [Restaurant Name]. ¿Cómo puedo ayudarle hoy?" :
                formData.language.startsWith('fr') ?
                  "Bonjour, bienvenue chez [Restaurant Name]. Comment puis-je vous aider aujourd'hui?" :
                formData.language.startsWith('de') ?
                  "Guten Tag, willkommen bei [Restaurant Name]. Wie kann ich Ihnen heute helfen?" :
                  "Hello, welcome to [Restaurant Name]. How can I help you today?"
              }
              value={formData.customWelcomeMessage}
              onChange={(e) => setFormData({ ...formData, customWelcomeMessage: e.target.value })}
              helperText="Leave empty to use the default greeting in your selected language"
            />
          </Card>
        </Grid>
      </Grid>
    </motion.div>
  );

  const renderStep5 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Business Integration
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure order management and reservation systems
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Orders/Delivery */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              <DeliveryDiningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Orders & Delivery
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.handlesOrders}
                  onChange={(e) => setFormData({ ...formData, handlesOrders: e.target.checked })}
                />
              }
              label="Handle order taking"
            />
            
            {formData.handlesOrders && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Select your ordering system:
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Select
                    value={formData.orderSystem}
                    onChange={(e) => setFormData({ ...formData, orderSystem: e.target.value })}
                    displayEmpty
                  >
                    <MenuItem value="">Select system...</MenuItem>
                    {orderSystems.map((system) => (
                      <MenuItem key={system.value} value={system.value}>
                        <Box>
                          <Typography variant="body2">{system.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {system.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {formData.orderSystem && formData.orderSystem !== 'other' && (
                  <TextField
                    fullWidth
                    label="API Key"
                    placeholder="Enter your API key"
                    value={formData.orderApiKey}
                    onChange={(e) => setFormData({ ...formData, orderApiKey: e.target.value })}
                    InputProps={{
                      startAdornment: <ApiIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    helperText="Found in your platform's developer settings"
                  />
                )}
                
                {formData.orderSystem === 'other' && (
                  <TextField
                    fullWidth
                    label="Notification Email"
                    placeholder="orders@yourrestaurant.com"
                    value={formData.notificationEmail}
                    onChange={(e) => setFormData({ ...formData, notificationEmail: e.target.value })}
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    helperText="Orders will be sent to this email"
                  />
                )}
                
                {/* Order Type Selection */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    Order types to handle:
                  </Typography>
                  <FormControl component="fieldset" sx={{ mt: 1 }}>
                    <FormGroup>
                      {[
                        { value: 'pickup', label: 'Pickup Orders', description: 'Customers collect their order' },
                        { value: 'delivery', label: 'Delivery Orders', description: 'Orders delivered to customer address' }
                      ].map((type) => (
                        <FormControlLabel
                          key={type.value}
                          control={
                            <Checkbox
                              checked={formData.orderTypes.includes(type.value)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    orderTypes: [...formData.orderTypes, type.value]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    orderTypes: formData.orderTypes.filter(t => t !== type.value)
                                  });
                                }
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">{type.label}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {type.description}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </Box>
                
                {/* Data Collection Options for Orders */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    Data to collect for orders:
                  </Typography>
                  <FormControl component="fieldset" sx={{ mt: 1 }}>
                    <FormGroup>
                      {[
                        { value: 'customer_name', label: 'Customer Name' },
                        { value: 'customer_phone', label: 'Customer Phone Number' },
                        { value: 'customer_email', label: 'Customer Email' },
                        { value: 'order_items', label: 'Order Items' },
                        { value: 'delivery_address', label: 'Delivery Address', deliveryOnly: true },
                        { value: 'special_instructions', label: 'Special Instructions' },
                        { value: 'preferred_delivery_time', label: 'Preferred Delivery Time', deliveryOnly: true }
                      ].filter(field => !field.deliveryOnly || formData.orderTypes.includes('delivery')).map((field) => (
                        <FormControlLabel
                          key={field.value}
                          control={
                            <Checkbox
                              checked={formData.orderDataFields.includes(field.value)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    orderDataFields: [...formData.orderDataFields, field.value]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    orderDataFields: formData.orderDataFields.filter(f => f !== field.value)
                                  });
                                }
                              }}
                            />
                          }
                          label={field.label}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </Box>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Reservations */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              <BookOnlineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Reservations
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.handlesReservations}
                  onChange={(e) => setFormData({ ...formData, handlesReservations: e.target.checked })}
                />
              }
              label="Handle reservations"
            />
            
            {formData.handlesReservations && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Select your reservation system:
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Select
                    value={formData.reservationSystem}
                    onChange={(e) => setFormData({ ...formData, reservationSystem: e.target.value })}
                    displayEmpty
                  >
                    <MenuItem value="">Select system...</MenuItem>
                    {reservationSystems.map((system) => (
                      <MenuItem key={system.value} value={system.value}>
                        <Box>
                          <Typography variant="body2">{system.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {system.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {formData.reservationSystem && formData.reservationSystem !== 'other' && (
                  <TextField
                    fullWidth
                    label="API Key"
                    placeholder="Enter your API key"
                    value={formData.reservationApiKey}
                    onChange={(e) => setFormData({ ...formData, reservationApiKey: e.target.value })}
                    InputProps={{
                      startAdornment: <ApiIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    helperText="Found in your platform's developer settings"
                  />
                )}
                
                {formData.reservationSystem === 'other' && (
                  <TextField
                    fullWidth
                    label="Notification Email"
                    placeholder="reservations@yourrestaurant.com"
                    value={formData.notificationEmail}
                    onChange={(e) => setFormData({ ...formData, notificationEmail: e.target.value })}
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    helperText="Reservations will be sent to this email"
                  />
                )}
                
                {/* Data Collection Options for Reservations */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    Data to collect for reservations:
                  </Typography>
                  <FormControl component="fieldset" sx={{ mt: 1 }}>
                    <FormGroup>
                      {[
                        { value: 'customer_name', label: 'Customer Name' },
                        { value: 'customer_phone', label: 'Customer Phone Number' },
                        { value: 'customer_email', label: 'Customer Email' },
                        { value: 'reservation_date', label: 'Reservation Date' },
                        { value: 'reservation_time', label: 'Reservation Time' },
                        { value: 'party_size', label: 'Number of People' },
                        { value: 'special_requests', label: 'Special Requests' },
                        { value: 'occasion', label: 'Occasion (Birthday, Anniversary, etc.)' }
                      ].map((field) => (
                        <FormControlLabel
                          key={field.value}
                          control={
                            <Checkbox
                              checked={formData.reservationDataFields.includes(field.value)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    reservationDataFields: [...formData.reservationDataFields, field.value]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    reservationDataFields: formData.reservationDataFields.filter(f => f !== field.value)
                                  });
                                }
                              }}
                            />
                          }
                          label={field.label}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </Box>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Human Escalation */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              <SupportAgentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Human Escalation
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.humanEscalationEnabled}
                  onChange={(e) => setFormData({ ...formData, humanEscalationEnabled: e.target.checked })}
                />
              }
              label="Enable human escalation"
            />
            
            {formData.humanEscalationEnabled && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Phone number to transfer calls to:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CountrySelector 
                    value={formData.humanEscalationCountry}
                    onChange={(code) => setFormData({ ...formData, humanEscalationCountry: code })}
                    sx={{ width: '120px', mr: 1 }}
                  />
                  <TextField
                    fullWidth
                    label="Phone Number"
                    placeholder="Enter phone number"
                    value={formData.humanEscalationPhone}
                    onChange={(e) => setFormData({ ...formData, humanEscalationPhone: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Number where calls should be transferred when customers request human assistance"
                  />
                </Box>
                
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>How it works:</strong> When customers say phrases like "I want to speak to someone", 
                    "Can I talk to a human", or "Transfer me to a person", the agent will automatically 
                    transfer the call to this number.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Info Card */}
        <Grid item xs={12}>
          <Alert severity="info">
            <Typography variant="body2">
              💡 <strong>Integration Help:</strong> Don't have API keys? Our support team can help you set up integrations.
              You can also configure these later in the agent settings.
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </motion.div>
  );

  const renderStep6 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      {success ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 3 }} />
          <Typography variant="h4" gutterBottom>
            🎉 Agent Deployed Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your voice agent is now live and ready to assist your customers
          </Typography>
          <Stack spacing={2} alignItems="center">
            <Typography variant="body2">
              Redirecting to agents dashboard...
            </Typography>
            <CircularProgress size={24} />
          </Stack>
        </Box>
      ) : (
        <>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Ready to Deploy?
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Review your configuration and deploy your voice agent
            </Typography>
          </Box>

          <Card sx={{ maxWidth: 800, mx: 'auto' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" gutterBottom>
                Agent Configuration Summary
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Agent Name" 
                        secondary={formData.agentName}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        {phoneNumberOptions.find(p => p.value === formData.phoneNumberType)?.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary="Phone Number Type" 
                        secondary={phoneNumberOptions.find(p => p.value === formData.phoneNumberType)?.label}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <LanguageIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Language" 
                        secondary={languageOptions.find(l => l.value === formData.language)?.label}
                      />
                    </ListItem>
                  </List>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <SmartToyIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="AI Model" 
                        secondary={modelOptions.find(m => m.value === formData.llmModel)?.label}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <DeliveryDiningIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Orders" 
                        secondary={formData.handlesOrders ? `Enabled (${formData.orderSystem}) - ${formData.orderTypes.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(' & ')}` : 'Disabled'}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <BookOnlineIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Reservations" 
                        secondary={formData.handlesReservations ? `Enabled (${formData.reservationSystem})` : 'Disabled'}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, p: 2, bgcolor: 'success.light', color: 'success.contrastText', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  🚀 Your agent will be deployed in approximately 2-3 minutes
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </motion.div>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      default: return renderStep1();
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/agents')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" gutterBottom>
            Plug & Play Voice Agent
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Deploy a voice agent in minutes with zero technical setup
          </Typography>
        </Box>
      </Box>

      {/* Progress Indicators */}
      {!success && (
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" spacing={2} justifyContent="center">
            {[1, 2, 3, 4, 5, 6].map((stepNumber) => (
              <Box
                key={stepNumber}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: step >= stepNumber ? 'primary.main' : 'grey.300',
                  color: step >= stepNumber ? 'white' : 'grey.600',
                  fontWeight: 600
                }}
              >
                {stepNumber}
              </Box>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
            Step {step} of 6
          </Typography>
        </Box>
      )}

      {/* Main Content */}
      {renderCurrentStep()}

      {/* Navigation Buttons */}
      {!success && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, maxWidth: 600, mx: 'auto' }}>
          {step > 1 && (
            <Button 
              variant="outlined" 
              onClick={handleBack}
              disabled={saving}
            >
              Back
            </Button>
          )}
          
          {step < 6 ? (
            <Button 
              variant="contained" 
              onClick={handleNext}
              disabled={!validateCurrentStep()}
              sx={{ ml: 'auto' }}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleDeploy}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <RocketLaunchIcon />}
              sx={{ ml: 'auto' }}
            >
              {saving ? 'Deploying...' : 'Deploy Agent'}
            </Button>
          )}
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlugAndPlay; 